import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  AlertTriangle, Shield, FileText, Users, Eye, Clock, CheckCircle2,
  ArrowRight, Bell,
  ChevronRight, ChevronUp, ChevronDown, Flag, UserCheck, DollarSign, Search,
  RefreshCw, Timer, XCircle, ArrowUpDown
} from 'lucide-react'

import { getLocalApiUrl, authFetch } from '../../config/api'

/* ─── BRAND ─── */
const T = { small: 12, normal: 14 } // typography: smallest 12px, normal 14px
const C = {
  pri: '#0a5169', priD: '#07384a', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc', border: '#e2e8f0',
}

const OPERATIONAL_GOALS = {
  smrOverdueMax: 0,
  smrAvgAgingDaysMax: 3,
  ecddAvgDaysMax: 14,
  alertsUnreviewedMax: 10,
}

function buildStatsFromSummary(stats, deltas, period = 'today') {
  const s = stats || {}
  const d = deltas || {}
  const alertLabel = period === 'week' ? 'Alerts (7 days)' : period === 'month' ? 'Alerts (30 days)' : 'Unreviewed Alerts'
  const payoutLabel = period === 'week' ? 'Payouts (7 days)' : period === 'month' ? 'Payouts (30 days)' : 'Payouts today'
  const alertsForUrgent = period === 'today' ? (s.alertsUnreviewed ?? 0) : (s.alertsOpen ?? s.alertsUnreviewed ?? 0)
  return [
    { id: 'alerts', label: alertLabel, value: s.alertsUnreviewed ?? 0, delta: d.alertsUnreviewed ?? 0, color: C.amber, bg: C.amberBg, icon: Bell, href: '/monitor-safe/alerts', linkLabel: 'Alert Queue', urgent: alertsForUrgent > OPERATIONAL_GOALS.alertsUnreviewedMax, target: `Target: ≤${OPERATIONAL_GOALS.alertsUnreviewedMax}` },
    { id: 'smr', label: 'SMR pipeline (open)', value: s.smrPipeline ?? 0, delta: d.smr ?? 0, color: C.red, bg: C.redBg, icon: FileText, href: '/compliance-safe/investigations/pipeline', linkLabel: 'SMR pipeline', urgent: (s.smrPipeline ?? 0) > 0, target: 'Cases with suspicion formed, no SMR filed' },
    { id: 'ecdd', label: 'Open ECDDs', value: s.ecddOpen ?? 0, delta: d.ecdd ?? 0, color: C.purple, bg: C.purpleBg, icon: Shield, href: '/compliance-safe/ecdd', linkLabel: 'ECDD', urgent: false, target: `Avg completion: ≤${OPERATIONAL_GOALS.ecddAvgDaysMax} days` },
    { id: 'invest', label: 'Active Investigations', value: s.investigationsActive ?? 0, delta: d.invest ?? 0, color: C.priL, bg: C.priM, icon: Search, href: '/compliance-safe/investigations', linkLabel: 'Investigation', urgent: false, target: 'Stalled: 0' },
    { id: 'watchlist', label: 'Watchlist (open)', value: s.watchlistMatches ?? 0, delta: d.watchlist ?? 0, color: C.red, bg: C.redBg, icon: Eye, href: '/monitor-safe/watch-list', linkLabel: 'Watchlist', urgent: (s.watchlistMatches ?? 0) > 0 },
    { id: 'kyc', label: 'KYC renewals due', value: s.kycRenewalsDue ?? 0, delta: d.kyc ?? 0, color: C.amber, bg: C.amberBg, icon: UserCheck, href: '/compliance-safe/kyc-registry', linkLabel: 'KYC Registry', urgent: false, target: 'From registry when available' },
    { id: 'highrisk', label: 'High risk patrons', value: s.highRiskPatrons ?? 0, delta: d.highrisk ?? 0, color: C.red, bg: C.redBg, icon: Flag, href: '/governance-safe/risk-config', linkLabel: 'Risk Config', urgent: false },
    { id: 'payouts', label: payoutLabel, value: s.payoutsInPeriod ?? s.payoutsToday ?? 0, delta: d.payouts ?? 0, color: C.green, bg: C.greenBg, icon: DollarSign, href: '/payout-safe/monitoring', linkLabel: 'Payout Dashboard', urgent: false },
    { id: 'visitors', label: 'Visitors in payout', value: s.visitorsInPayout ?? 0, delta: d.visitors ?? 0, color: C.amber, bg: C.amberBg, icon: Users, href: '/profile-safe/visitors', linkLabel: 'Visitors', urgent: false },
  ]
}

/** Open the investigation workspace for an SMR case (case_number or internal id). */
function smrInvestigationHref(caseRef, tab = 'smr_form') {
  if (!caseRef) return '/compliance-safe/investigations/pipeline'
  const qs = tab ? `?tab=${encodeURIComponent(tab)}` : ''
  return `/compliance-safe/investigations/${encodeURIComponent(String(caseRef))}${qs}`
}

/** Primary destination for “Review SMRs” — most urgent open case, else pipeline. */
function smrReviewLandingHref(smrs) {
  if (!smrs?.length) return '/compliance-safe/investigations/pipeline'
  const pick = smrs.find((s) => s.urgency === 'overdue')
    || smrs.find((s) => s.urgency === 'critical')
    || smrs[0]
  return smrInvestigationHref(pick.id)
}

const severityMeta = {
  critical: { color: C.red, bg: C.redBg, label: 'Critical' },
  high: { color: C.amber, bg: C.amberBg, label: 'High' },
  medium: { color: C.pri, bg: C.priM, label: 'Medium' },
  low: { color: C.slate, bg: C.slateBg, label: 'Low' },
}

const obligationMeta = {
  overdue: { color: C.red, bg: C.redBg, icon: XCircle, label: 'Overdue' },
  warning: { color: C.amber, bg: C.amberBg, icon: AlertTriangle, label: 'Attention' },
  ok: { color: C.green, bg: C.greenBg, icon: CheckCircle2, label: 'OK' },
}

const activityMeta = {
  alert: { color: C.amber, icon: Bell },
  invest: { color: C.pri, icon: Search },
  kyc: { color: C.green, icon: UserCheck },
  smr: { color: C.red, icon: FileText },
  import: { color: C.slate, icon: RefreshCw },
}

function StatCard({ stat, onClick }) {
  const Icon = stat.icon
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg p-4 cursor-pointer transition-all relative overflow-hidden border border-slate-200"
    >
      {stat.urgent && (
        <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full" style={{ background: stat.color }} />
      )}
      <div className="flex items-start mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: stat.bg, border: `1px solid ${stat.color}22` }}>
          <Icon size={16} color={stat.color} />
        </div>
      </div>
      <div className="text-2xl font-extrabold leading-none mb-1" style={{ color: stat.urgent ? stat.color : '#1e293b' }}>{stat.value}</div>
      <div className="text-slate-500 font-medium" style={{ fontSize: T.normal }}>{stat.label}</div>
      {stat.target && <div className="text-slate-400 mt-1" style={{ fontSize: T.small }}>{stat.target}</div>}
    </div>
  )
}

function SMRBanner({ smrs, onReview, onOpenCase }) {
  if (!smrs.length) return null
  const overdue = smrs.filter(s => s.urgency === 'overdue')
  const critical = smrs.filter(s => s.urgency === 'critical')
  const hasOverdue = overdue.length > 0

  return (
    <div
      className="rounded-lg p-4 mb-5 flex items-center gap-4"
      style={{
        background: hasOverdue ? C.redBg : C.amberBg,
        border: `1px solid ${hasOverdue ? C.redBd : C.amberBd}`,
        borderLeft: `4px solid ${hasOverdue ? C.red : C.amber}`,
      }}
    >
      <Timer size={20} color={hasOverdue ? C.red : C.amber} className="flex-shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-extrabold mb-1" style={{ color: hasOverdue ? C.red : C.amber }}>
          {hasOverdue ? `⚠ ${overdue.length} SMR deadline OVERDUE — immediate action required` : `${critical.length} SMR deadline within 24 hours`}
        </div>
        <div className="flex gap-3 flex-wrap">
          {smrs.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => onOpenCase?.(s)}
              className="font-semibold px-2.5 py-0.5 rounded-full cursor-pointer hover:opacity-90 transition-opacity"
              style={{
                fontSize: T.small,
                background: s.urgency === 'overdue' ? C.red : s.urgency === 'critical' ? C.amber : '#fff',
                color: s.urgency === 'overdue' || s.urgency === 'critical' ? '#fff' : C.amber,
                border: s.urgency === 'warning' ? `1px solid ${C.amberBd}` : 'none',
              }}
              title={`Open ${s.id} — SMR form`}
            >
              {s.id} · {s.patron}
              {s.urgency === 'overdue' ? ' · OVERDUE' : s.urgency === 'critical' ? ' · 1 day left' : ` · ${s.daysLeft} days`}
              {s.type === 'terrorism' ? ' ⚡ TF' : ''}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onReview}
        className="px-4 py-2 rounded-md font-bold text-white flex items-center gap-1.5 whitespace-nowrap"
        style={{ fontSize: T.normal, background: hasOverdue ? C.red : C.amber }}
      >
        Review SMRs <ArrowRight size={13} />
      </button>
    </div>
  )
}

function AlertRow({ alert, i, listLen, onClick }) {
  const sev = severityMeta[alert.severity] || severityMeta.medium
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3.5 py-3 px-4 cursor-pointer transition-colors ${i < listLen - 1 ? 'border-b border-slate-100' : ''}`}
      style={{ background: alert.status === 'new' && alert.severity === 'critical' ? '#fffaf9' : '#fff' }}
    >
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sev.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-bold text-slate-800">{alert.patron}</span>
          <span className="font-bold px-1.5 py-0.5 rounded-full" style={{ fontSize: T.small, color: sev.color, background: sev.bg, border: `1px solid ${sev.color}33` }}>{sev.label}</span>
          {alert.status === 'investigating' && (
            <span className="font-bold px-1.5 py-0.5 rounded-full" style={{ fontSize: T.small, color: C.pri, background: C.priM }}>Investigating</span>
          )}
        </div>
        <div className="text-slate-500" style={{ fontSize: T.small }}>{alert.rule}</div>
      </div>
      <div className="text-center flex-shrink-0">
        <div className="text-sm font-extrabold" style={{ color: alert.risk >= 75 ? C.red : alert.risk >= 55 ? C.amber : C.green }}>{alert.risk}</div>
        <div className="text-slate-400 uppercase tracking-wider" style={{ fontSize: T.small }}>Risk</div>
      </div>
      <div className="text-right flex-shrink-0 min-w-[70px]">
        <div className="text-sm font-bold text-slate-800">{alert.amount}</div>
        <div className="text-slate-400" style={{ fontSize: T.small }}>{alert.time}</div>
      </div>
      <ChevronRight size={14} className="text-slate-400" />
    </div>
  )
}

function ObligationRow({ ob }) {
  const meta = obligationMeta[ob.status]
  const Icon = meta.icon
  return (
    <div className="flex items-center gap-3 py-2.5 px-4 border-b border-slate-50 cursor-pointer">
      <Icon size={15} color={meta.color} className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800" style={{ fontSize: T.normal }}>{ob.label}</div>
        <div className="text-slate-400" style={{ fontSize: T.small }}>{ob.ref}</div>
      </div>
      <span className="font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ fontSize: T.small, color: meta.color, background: meta.bg, border: `1px solid ${meta.color}22` }}>
        {ob.count}
      </span>
    </div>
  )
}

const GRID_COL = {
  rowNum: 'w-9 min-w-[36px]',
  venue: 'min-w-[140px]',
  location: 'min-w-[100px]',
  num: 'min-w-[72px] w-[88px]',
  amount: 'min-w-[96px] w-[110px]',
  status: 'min-w-[88px] w-[96px]',
}

/** Spreadsheet-style venue/zone payout summary (aligned with Payout Monitoring tables). */
function PayoutVenueGrid({ rows, nameColumn = 'Venue', onRowClick, emptyMessage = 'No payouts in this period.' }) {
  const [sortKey, setSortKey] = useState('payouts')
  const [sortDir, setSortDir] = useState('desc')

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  const sorted = useMemo(() => {
    const list = [...(rows || [])]
    const dir = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      if (sortKey === 'name') return dir * String(a.name || '').localeCompare(String(b.name || ''))
      if (sortKey === 'topAmount') {
        const pa = parseAmountSort(a.topAmount)
        const pb = parseAmountSort(b.topAmount)
        return dir * (pa - pb)
      }
      const av = Number(a[sortKey]) || 0
      const bv = Number(b[sortKey]) || 0
      return dir * (av - bv)
    })
    return list
  }, [rows, sortKey, sortDir])

  const totals = useMemo(() => ({
    payouts: (rows || []).reduce((s, r) => s + (Number(r.payouts) || 0), 0),
    visitors: (rows || []).reduce((s, r) => s + (Number(r.visitors) || 0), 0),
  }), [rows])

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={11} className="opacity-35" />
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
  }

  const thClass =
    'px-2.5 py-2 text-left font-semibold text-slate-600 border border-slate-300 bg-slate-100 select-none whitespace-nowrap'
  const tdClass = 'px-2.5 py-1.5 border border-slate-200 text-slate-800 align-middle whitespace-nowrap'
  const tdNum = `${tdClass} text-right tabular-nums font-medium`

  if (!sorted.length) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 py-10 text-center text-slate-500" style={{ fontSize: T.normal }}>
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-md border border-slate-300 shadow-sm">
      <table className="w-full border-collapse text-[13px] leading-tight">
        <thead>
          <tr>
            <th className={`${thClass} ${GRID_COL.rowNum} text-center sticky left-0 z-10`}>#</th>
            <th
              className={`${thClass} ${GRID_COL.venue} cursor-pointer hover:bg-slate-200/80 sticky left-9 z-10 border-r-2 border-slate-300`}
              onClick={() => toggleSort('name')}
            >
              <span className="inline-flex items-center gap-1">{nameColumn} <SortIcon col="name" /></span>
            </th>
            <th className={`${thClass} ${GRID_COL.location}`}>Location</th>
            <th
              className={`${thClass} ${GRID_COL.num} text-right cursor-pointer hover:bg-slate-200/80`}
              onClick={() => toggleSort('payouts')}
            >
              <span className="inline-flex items-center justify-end gap-1 w-full">Payouts <SortIcon col="payouts" /></span>
            </th>
            <th
              className={`${thClass} ${GRID_COL.num} text-right cursor-pointer hover:bg-slate-200/80`}
              onClick={() => toggleSort('visitors')}
            >
              <span className="inline-flex items-center justify-end gap-1 w-full">Visitors <SortIcon col="visitors" /></span>
            </th>
            <th
              className={`${thClass} ${GRID_COL.amount} text-right cursor-pointer hover:bg-slate-200/80`}
              onClick={() => toggleSort('topAmount')}
            >
              <span className="inline-flex items-center justify-end gap-1 w-full">Top amount <SortIcon col="topAmount" /></span>
            </th>
            <th className={`${thClass} ${GRID_COL.status} text-center`}>Status</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => {
            const zebra = i % 2 === 1
            const flagged = row.flag
            const rowBg = flagged ? C.redBg : zebra ? '#f8fafc' : '#fff'
            const stickyBg = flagged ? C.redBg : zebra ? '#f8fafc' : '#fff'
            return (
              <tr
                key={row.key}
                onClick={() => onRowClick?.(row)}
                className={`cursor-pointer group ${flagged ? '' : 'hover:bg-[#e8f2f6]'}`}
                style={{ background: rowBg }}
              >
                <td
                  className={`${tdClass} ${GRID_COL.rowNum} text-center text-slate-400 tabular-nums sticky left-0 z-[1] border-r border-slate-200`}
                  style={{ background: stickyBg }}
                >
                  {i + 1}
                </td>
                <td
                  className={`${tdClass} ${GRID_COL.venue} font-semibold sticky left-9 z-[1] border-r-2 border-slate-300`}
                  style={{ background: stickyBg, color: flagged ? C.red : '#1e293b' }}
                >
                  <span className="inline-flex items-center gap-1.5 max-w-[200px] truncate" title={row.name}>
                    {row.name}
                    {flagged && <AlertTriangle size={12} color={C.red} className="flex-shrink-0" />}
                  </span>
                </td>
                <td className={`${tdClass} ${GRID_COL.location} text-slate-500 truncate max-w-[140px]`} title={row.location || undefined}>
                  {row.location || '—'}
                </td>
                <td className={tdNum}>{row.payouts ?? 0}</td>
                <td className={tdNum}>{row.visitors ?? 0}</td>
                <td className={`${tdNum} font-semibold`}>{row.topAmount || '—'}</td>
                <td className={`${tdClass} ${GRID_COL.status} text-center`}>
                  {flagged ? (
                    <span className="inline-block px-1.5 py-0.5 rounded font-bold text-red-700 bg-red-100 border border-red-200" style={{ fontSize: T.small }}>
                      Review
                    </span>
                  ) : (
                    <span className="text-slate-400" style={{ fontSize: T.small }}>OK</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
            <td colSpan={3} className={`${tdClass} text-slate-700 sticky left-0 z-[1] bg-slate-100 border-r-2 border-slate-300`}>
              Total ({sorted.length} {sorted.length === 1 ? 'row' : 'rows'})
            </td>
            <td className={`${tdNum} bg-slate-100`}>{totals.payouts}</td>
            <td className={`${tdNum} bg-slate-100`}>{totals.visitors}</td>
            <td colSpan={2} className={`${tdClass} bg-slate-100 text-slate-500`} style={{ fontSize: T.small }}>
              Click a row for transaction log
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function parseAmountSort(v) {
  if (v == null || v === '—') return 0
  const n = Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

function SectionHeader({ title, icon, action, actionHref, badge }) {
  return (
    <div className="flex items-center justify-between py-3.5 px-4 border-b border-slate-100 bg-slate-50">
      <div className="flex items-center gap-2">
        <span style={{ fontSize: T.normal }}>{icon}</span>
        <span className="font-extrabold text-slate-800" style={{ fontSize: T.normal }}>{title}</span>
        {badge !== undefined && (
          <span className="font-bold px-2 py-0.5 rounded-full" style={{ fontSize: T.small, color: C.red, background: C.redBg, border: `1px solid ${C.redBd}` }}>{badge}</span>
        )}
      </div>
      {action && (
        actionHref ? (
          <Link to={actionHref} className="font-bold px-3 py-1 rounded-md cursor-pointer flex items-center gap-1" style={{ fontSize: T.small, color: C.pri, background: C.priM, border: `1px solid ${C.pri}33` }}>
            {action} <ChevronRight size={11} />
          </Link>
        ) : (
          <button className="font-bold px-3 py-1 rounded-md cursor-pointer flex items-center gap-1" style={{ fontSize: T.small, color: C.pri, background: C.priM, border: `1px solid ${C.pri}33` }}>
            {action} <ChevronRight size={11} />
          </button>
        )
      )}
    </div>
  )
}

function Card({ children, className = '' }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg overflow-hidden ${className}`}>
      {children}
    </div>
  )
}

export default function ComplianceDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState('today')
  const [payoutView, setPayoutView] = useState('venue')
  const [selectedVenue, setSelectedVenue] = useState('')
  const [summary, setSummary] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const loadSummary = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const url = getLocalApiUrl(`/api/compliance/operations-summary?period=${encodeURIComponent(selectedPeriod)}`)
      const res = await authFetch(url)
      const j = await res.json()
      if (!res.ok || !j.success) {
        setSummary(null)
        setLoadError(j.error || `Request failed (${res.status})`)
        return
      }
      setSummary(j.data)
    } catch (e) {
      setSummary(null)
      setLoadError(e?.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }, [selectedPeriod])

  useEffect(() => { loadSummary() }, [loadSummary])

  const activePeriod = summary?.period || selectedPeriod
  const periodLabel = summary?.periodLabel || (selectedPeriod === 'week' ? 'Last 7 days' : selectedPeriod === 'month' ? 'Last 30 days' : 'Today')

  const stats = useMemo(
    () => buildStatsFromSummary(summary?.stats, summary?.deltas, activePeriod),
    [summary, activePeriod]
  )

  const smrDeadlines = summary?.smrDeadlines ?? []
  const alertsPreview = summary?.alertsPreview ?? []
  const obligations = summary?.obligations ?? []
  const payoutVenues = summary?.payoutByVenue ?? []
  const payoutZones = useMemo(
    () => payoutVenues.map((v) => ({ zone: v.venue, payouts: v.payouts, visitors: v.visitors, topAmount: v.topAmount, flag: v.flag })),
    [payoutVenues]
  )
  const recentActivity = summary?.recentActivity ?? []
  const investigationsPreview = summary?.investigationsPreview ?? []
  const highRiskPreview = summary?.highRiskPreview ?? []
  const payoutTrend = summary?.payoutTrend
  const payoutTrendMode = payoutTrend?.mode || (activePeriod === 'today' ? 'hour' : 'day')
  const payoutTrendBuckets = payoutTrend?.values ?? summary?.payoutHourly ?? (payoutTrendMode === 'hour' ? new Array(24).fill(0) : [])
  const payoutTrendLabels = payoutTrend?.labels ?? []
  const payoutTrendMax = Math.max(1, payoutTrend?.max ?? summary?.payoutHourlyMax ?? 1)
  const payoutSectionTitle = activePeriod === 'week' ? 'Payout Activity — Last 7 days' : activePeriod === 'month' ? 'Payout Activity — Last 30 days' : 'Payout Activity Today'
  const payoutTrendTitle = payoutTrendMode === 'hour' ? 'Hourly Payout Volume' : 'Daily Payout Volume'
  const kpis = summary?.kpis || {}

  const venueLabel = summary?.venueLabel || '—'
  const generatedLabel = summary?.generatedAt
    ? new Date(summary.generatedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'

  const payoutGridRows = useMemo(() => {
    if (payoutView === 'zone') {
      return [...payoutZones]
        .sort((a, b) => b.payouts - a.payouts)
        .map((z) => ({
          key: z.zone,
          name: z.zone,
          location: '',
          payouts: z.payouts,
          visitors: z.visitors,
          topAmount: z.topAmount,
          flag: z.flag,
          venueId: null,
        }))
    }
    const venues = selectedVenue
      ? payoutVenues.filter((v) => v.id === selectedVenue)
      : [...payoutVenues].sort((a, b) => b.payouts - a.payouts)
    return venues.map((v) => ({
      key: v.id,
      name: v.venue,
      location: v.location || '',
      payouts: v.payouts,
      visitors: v.visitors,
      topAmount: v.topAmount,
      flag: v.flag,
      venueId: v.id,
    }))
  }, [payoutView, payoutZones, payoutVenues, selectedVenue])

  const headerBadges = useMemo(() => {
    const smrO = kpis.smrOverdue ?? 0
    const a = summary?.stats?.alertsUnreviewed ?? 0
    const e = summary?.stats?.ecddOpen ?? 0
    const out = []
    if (smrO > 0) out.push({ label: `${smrO} SMR overdue`, color: C.red, urgent: true })
    const alertsOpen = summary?.stats?.alertsOpen ?? a
    if (activePeriod === 'today' && alertsOpen > 0) {
      out.push({ label: `${alertsOpen} alerts open`, color: C.amber, urgent: alertsOpen > OPERATIONAL_GOALS.alertsUnreviewedMax })
    } else if (activePeriod !== 'today' && a > 0) {
      out.push({ label: `${a} alerts in period`, color: C.amber, urgent: false })
    }
    if (e > 0) out.push({ label: `${e} ECDD open`, color: C.purple, urgent: false })
    out.push({ label: `${periodLabel} · ${venueLabel} · ${generatedLabel}`, color: C.green, urgent: false })
    return out
  }, [kpis.smrOverdue, summary?.stats, generatedLabel, venueLabel, activePeriod, periodLabel])

  const statClick = (stat) => {
    if (stat.href) navigate(stat.href)
  }

  const alertClick = (alert) => {
    if (alert.alertId) navigate(`/monitor-safe/alerts?highlight=${encodeURIComponent(alert.alertId)}`)
    else navigate('/monitor-safe/alerts')
  }

  if (loading && !summary) {
    return (
      <div className="min-h-full p-8 text-slate-500 font-medium">Loading compliance dashboard…</div>
    )
  }

  if (loadError && !summary) {
    return (
      <div className="min-h-full p-8">
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 mb-4">{loadError}</div>
        <button type="button" className="font-bold px-4 py-2 rounded-md text-white" style={{ background: C.pri }} onClick={loadSummary}>Retry</button>
      </div>
    )
  }

  return (
    <div className="min-h-full">
      {/* ── Operational goals banner ── */}
      <div className="mb-4 p-3.5 rounded-lg flex items-center gap-3" style={{ background: C.priM, border: `1px solid ${C.pri}33` }}>
        <Shield size={18} color={C.pri} className="flex-shrink-0" />
        <div>
          <div className="font-bold text-slate-800" style={{ fontSize: T.normal }}>Operational targets</div>
          <div className="text-slate-600" style={{ fontSize: T.small }}>SMR: ≤3 days to file · 0 overdue · ECDD: ≤14 days avg · Alerts: ≤10 unreviewed</div>
        </div>
      </div>
      {/* ── Period filter + alert badges ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          {['today', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setSelectedPeriod(p)}
              className="px-3.5 py-1.5 rounded-md font-semibold cursor-pointer border"
              style={{
                fontSize: T.small,
                background: selectedPeriod === p ? C.pri : '#fff',
                color: selectedPeriod === p ? '#fff' : C.slate,
                borderColor: selectedPeriod === p ? C.pri : '#e2e8f0',
              }}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
          {loading && summary && (
            <span className="text-slate-400 font-medium" style={{ fontSize: T.small }}>Refreshing…</span>
          )}
        </div>
        <div className="flex gap-2.5 flex-wrap">
          {headerBadges.map(s => (
            <div
              key={s.label}
              className="rounded-full px-3.5 py-1 font-bold flex items-center gap-1.5"
              style={{
                fontSize: T.small,
                background: s.urgent ? s.color : C.slateBg,
                color: s.urgent ? '#fff' : C.slate,
                border: `1px solid ${s.urgent ? s.color : C.border}`,
              }}
            >
              {s.urgent && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              {s.label}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-9 gap-3 mb-4">
        {stats.map(s => (
          <StatCard key={s.id} stat={s} onClick={() => statClick(s)} />
        ))}
      </div>

      <SMRBanner
        smrs={smrDeadlines}
        onReview={() => navigate(smrReviewLandingHref(smrDeadlines))}
        onOpenCase={(s) => navigate(smrInvestigationHref(s.id))}
      />

      {/* ── Operational KPIs (SMR aging, ECDD time) ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-3.5 rounded-lg bg-slate-50 border border-slate-200">
        <div>
          <div className="text-slate-500 font-semibold" style={{ fontSize: T.small }}>Avg SMR aging</div>
          <div className="text-xl font-extrabold text-slate-800">{kpis.avgSmrAgingDays != null ? `${kpis.avgSmrAgingDays} days` : '—'}</div>
          <div className="text-slate-400" style={{ fontSize: T.small }}>Target: ≤{OPERATIONAL_GOALS.smrAvgAgingDaysMax} days</div>
        </div>
        <div>
          <div className="text-slate-500 font-semibold" style={{ fontSize: T.small }}>SMR overdue</div>
          <div className="text-xl font-extrabold" style={{ color: (kpis.smrOverdue ?? 0) > OPERATIONAL_GOALS.smrOverdueMax ? C.red : C.green }}>{kpis.smrOverdue ?? 0}</div>
          <div className="text-slate-400" style={{ fontSize: T.small }}>Target: {OPERATIONAL_GOALS.smrOverdueMax}</div>
        </div>
        <div>
          <div className="text-slate-500 font-semibold" style={{ fontSize: T.small }}>Avg ECDD time</div>
          <div className="text-xl font-extrabold text-slate-800">{kpis.avgEcddDays != null ? `${kpis.avgEcddDays} days` : '—'}</div>
          <div className="text-slate-400" style={{ fontSize: T.small }}>Target: ≤{OPERATIONAL_GOALS.ecddAvgDaysMax} days</div>
        </div>
        <div>
          <div className="text-slate-500 font-semibold" style={{ fontSize: T.small }}>Stalled cases</div>
          <div className="text-xl font-extrabold text-slate-800">{kpis.stalledCases ?? 0}</div>
          <div className="text-slate-400" style={{ fontSize: T.small }}>Target: 0</div>
        </div>
      </div>

      {/* ── MAIN 2-COLUMN ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 mb-5">
        <div>
          <Card className="mb-5">
            <SectionHeader
              title="Alert Queue"
              icon="🔔"
              action="View all"
              actionHref="/monitor-safe/alerts"
              badge={summary?.stats?.alertsUnreviewed ?? 0}
            />
            <div>
              {alertsPreview.length === 0 ? (
                <div className="py-8 px-4 text-slate-500 text-center" style={{ fontSize: T.normal }}>No open alerts.</div>
              ) : (
                alertsPreview.map((a, i) => (
                  <AlertRow key={a.id} alert={a} i={i} listLen={alertsPreview.length} onClick={() => alertClick(a)} />
                ))
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between py-3.5 px-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2">
                <span style={{ fontSize: T.normal }}>💳</span>
                <span className="font-extrabold text-slate-800" style={{ fontSize: T.normal }}>{payoutSectionTitle}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-md border border-slate-200 overflow-hidden">
                  {[
                    { id: 'zone', label: 'By Zone' },
                    { id: 'venue', label: 'By Venue' },
                  ].map(v => (
                    <button
                      key={v.id}
                      onClick={() => { setPayoutView(v.id); if (v.id === 'zone') setSelectedVenue('') }}
                      className="px-3 py-1.5 font-semibold"
                      style={{
                        fontSize: T.small,
                        background: payoutView === v.id ? C.pri : '#fff',
                        color: payoutView === v.id ? '#fff' : C.slate,
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                {payoutView === 'venue' && (
                  <select
                    value={selectedVenue}
                    onChange={e => setSelectedVenue(e.target.value)}
                    className="font-medium border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-psg-teal focus:border-transparent"
                    style={{ fontSize: T.normal, minWidth: 140 }}
                  >
                    <option value="">Top venues</option>
                    {payoutVenues.map(v => (
                      <option key={v.id} value={v.id}>{v.venue}</option>
                    ))}
                  </select>
                )}
                <Link to="/payout-safe/monitoring" className="font-bold px-3 py-1 rounded-md cursor-pointer flex items-center gap-1" style={{ fontSize: T.small, color: C.pri, background: C.priM, border: `1px solid ${C.pri}33` }}>
                  Transaction Log <ChevronRight size={11} />
                </Link>
              </div>
            </div>
            <div className="p-4">
              <PayoutVenueGrid
                rows={payoutGridRows}
                nameColumn={payoutView === 'zone' ? 'Zone' : 'Venue'}
                emptyMessage="No payout activity for this period."
                onRowClick={(row) => {
                  const q = row.venueId ? `?venue=${encodeURIComponent(row.name)}` : ''
                  navigate(`/payout-safe/monitoring${q}`)
                }}
              />

              <div className="mt-5 pt-4 border-t border-slate-200">
                <div className="font-bold text-slate-500 mb-2 uppercase tracking-wider" style={{ fontSize: T.small }}>{payoutTrendTitle}</div>
                <div className="flex items-end gap-0.5 h-12">
                  {payoutTrendBuckets.map((v, i) => {
                    const cnt = Number(v) || 0
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={payoutTrendLabels[i] || undefined}>
                        <div
                          className="w-full rounded-t-sm"
                          style={{
                            height: `${(cnt / payoutTrendMax) * 44}px`,
                            background: cnt > payoutTrendMax * 0.85 ? C.amber : cnt > payoutTrendMax * 0.5 ? C.priL : C.priM,
                          }}
                        />
                      </div>
                    )
                  })}
                </div>
                <div className="flex justify-between mt-1 text-slate-400" style={{ fontSize: T.small }}>
                  {payoutTrendMode === 'hour' ? (
                    <>
                      <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
                    </>
                  ) : payoutTrendLabels.length > 0 ? (
                    <>
                      <span>{payoutTrendLabels[0]}</span>
                      <span>{payoutTrendLabels[Math.floor(payoutTrendLabels.length / 2)]}</span>
                      <span>{payoutTrendLabels[payoutTrendLabels.length - 1]}</span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </Card>

          <Card className="mb-5">
            <SectionHeader
              title="KYC Renewals Due"
              icon="🪪"
              action="KYC registry"
              actionHref="/compliance-safe/kyc-registry"
              badge={summary?.stats?.kycRenewalsDue ?? 0}
            />
            <div className="p-4 text-slate-500" style={{ fontSize: T.normal }}>
              KYC renewal rows are not included in the operations summary yet. Open the registry for the live queue, or use the stat tile above when counts are wired.
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Active Investigations"
              icon="🔍"
              action="All investigations"
              actionHref="/compliance-safe/investigations"
              badge={summary?.stats?.investigationsActive ?? 0}
            />
            <div className="p-4">
              {investigationsPreview.length === 0 ? (
                <div className="text-slate-500 text-center py-6" style={{ fontSize: T.normal }}>No open investigations.</div>
              ) : (
                investigationsPreview.map((inv, i) => (
                  <div
                    key={inv.id}
                    onClick={() => navigate(`/compliance-safe/investigations/${encodeURIComponent(inv.id)}`)}
                    className="py-2.5 cursor-pointer"
                    style={{ borderBottom: i < investigationsPreview.length - 1 ? '1px solid #f8f8f8' : 'none' }}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div>
                        <span className="text-sm font-bold text-slate-800">{inv.patron}</span>
                        <span className="text-slate-400 ml-2 font-mono" style={{ fontSize: T.small }}>{inv.id}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {inv.outcome && (
                          <span className="font-bold px-2 py-0.5 rounded-full" style={{ fontSize: T.small, color: inv.outcome === 'SMR' ? C.red : C.purple, background: inv.outcome === 'SMR' ? C.redBg : C.purpleBg }}>→ {inv.outcome}</span>
                        )}
                        <span className="text-slate-400" style={{ fontSize: T.small }}>{inv.daysOpen === 0 ? 'Opened today' : `${inv.daysOpen}d open`}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                        <div className="h-full rounded transition-all" style={{ width: `${inv.progress}%`, background: inv.progress >= 80 ? C.green : inv.progress >= 40 ? C.priL : C.amber }} />
                      </div>
                      <span className="text-slate-500 whitespace-nowrap" style={{ fontSize: T.small }}>{inv.steps}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        <div>
          <Card className="mb-4">
            <SectionHeader title="AUSTRAC Obligations" icon="⚖️" action="View All" actionHref="/governance-safe/obligations" />
            <div>
              {obligations.map(ob => <ObligationRow key={ob.id} ob={ob} />)}
            </div>
            <div className="py-3 px-4 border-t border-slate-100">
              <div className="flex gap-4" style={{ fontSize: T.small }}>
                <span className="text-green-600 font-bold flex items-center gap-1"><CheckCircle2 size={11} /> {obligations.filter(o => o.status === 'ok').length} compliant</span>
                <span className="text-amber-600 font-bold flex items-center gap-1"><AlertTriangle size={11} /> {obligations.filter(o => o.status === 'warning').length} need attention</span>
                <span className="text-red-600 font-bold flex items-center gap-1"><XCircle size={11} /> {obligations.filter(o => o.status === 'overdue').length} overdue</span>
              </div>
            </div>
          </Card>

          <Card className="mb-4">
            <SectionHeader
              title="High Risk Patrons"
              icon="🚩"
              action="Risk config"
              actionHref="/governance-safe/risk-config"
              badge={summary?.stats?.highRiskPatrons ?? 0}
            />
            <div className="p-3">
              {highRiskPreview.length === 0 ? (
                <div className="text-slate-500 text-center py-6" style={{ fontSize: T.normal }}>No patrons at or above the high-risk threshold.</div>
              ) : (
                highRiskPreview.map((p, i) => (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/profile-safe/search?memberId=${encodeURIComponent(p.id)}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/profile-safe/search?memberId=${encodeURIComponent(p.id)}`) }}
                    className="flex items-center gap-2.5 py-2 cursor-pointer"
                    style={{ borderBottom: i < highRiskPreview.length - 1 ? '1px solid #f8f8f8' : 'none' }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center font-extrabold flex-shrink-0"
                      style={{
                        fontSize: T.small,
                        background: p.score >= 85 ? C.redBg : p.score >= 70 ? C.amberBg : C.priM,
                        color: p.score >= 85 ? C.red : p.score >= 70 ? C.amber : C.pri,
                        border: `2px solid ${p.score >= 85 ? C.redBd : p.score >= 70 ? C.amberBd : C.priM}`,
                      }}
                    >
                      {p.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-800 flex items-center gap-1.5" style={{ fontSize: T.normal }}>
                        {p.name}
                        {p.alert && <Bell size={10} color={C.amber} />}
                      </div>
                      <div className="text-slate-500" style={{ fontSize: T.small }}>{p.id} · {p.tag}</div>
                    </div>
                    <ChevronRight size={12} className="text-slate-400" />
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card>
            <SectionHeader title="Recent Activity" icon="🕐" />
            <div className="p-3">
              {recentActivity.length === 0 ? (
                <div className="text-slate-500 text-center py-6" style={{ fontSize: T.normal }}>No recent audit activity.</div>
              ) : (
                recentActivity.map((ev, i) => {
                  const meta = activityMeta[ev.type] || activityMeta.import
                  const Icon = meta.icon
                  return (
                    <div key={i} className="flex gap-2.5 mb-3.5">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}33` }}>
                        <Icon size={12} color={meta.color} />
                      </div>
                      <div>
                        <div className="text-slate-700 leading-snug" style={{ fontSize: T.small }}>
                          <strong>{ev.who}</strong> — {ev.action}
                        </div>
                        <div className="text-slate-400 mt-0.5" style={{ fontSize: T.small }}>{ev.time}</div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
